'use client';
import { supabase } from '@/lib/supabase';
import { getChatCompletion } from '@/lib/ai/chatCompletion';

// ─── Types ────────────────────────────────────────────────────────────────────

export type BillStatus = 'Paid' | 'Unpaid' | 'Due Soon' | 'Overdue';
export type BillOwnership = 'Personal' | 'Business';

export const BILL_CATEGORIES = [
  'Mortgage',
  'Rent',
  'Utilities',
  'Internet',
  'Cell Phone',
  'Insurance',
  'Credit Cards',
  'Loans',
  'Fuel',
  'Software',
  'Subscriptions',
  'Taxes',
  'Park-N-Lot',
  'Cree8tiveminds',
  'Cre8Loads',
  'Other',
] as const;

export type BillCategory = typeof BILL_CATEGORIES[number];

export interface Bill {
  id: string;
  user_id: string;
  bill_name: string;
  category: BillCategory;
  ownership: BillOwnership;
  company: string | null;
  monthly_amount: number;
  due_date: number; // day of month 1-31
  auto_pay: boolean;
  bill_status: BillStatus;
  notes: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface BillFormData {
  bill_name: string;
  category: BillCategory;
  ownership: BillOwnership;
  company: string;
  monthly_amount: number;
  due_date: number;
  auto_pay: boolean;
  bill_status: BillStatus;
  notes: string;
}

export interface BillDashboardStats {
  totalMonthly: number;
  dueTodayCount: number;
  dueThisWeekCount: number;
  dueThisMonthCount: number;
  overdueCount: number;
  overdueTotal: number;
  autoPayCount: number;
  autoPayTotal: number;
  paidCount: number;
  unpaidCount: number;
}

export interface BillNotification {
  billId: string;
  billName: string;
  amount: number;
  dueDate: number;
  daysUntilDue: number;
  type: 'overdue' | 'due_today' | 'due_3_days' | 'due_7_days';
}

export interface BillReport {
  type: 'monthly' | 'annual' | 'paid_vs_unpaid' | 'personal' | 'business';
  title: string;
  totalAmount: number;
  bills: Bill[];
  breakdown: { label: string; amount: number; count: number }[];
}

// ─── CRUD ─────────────────────────────────────────────────────────────────────

export async function getBills(userId: string): Promise<Bill[]> {
  const { data, error } = await supabase
    .from('bills')
    .select('*')
    .eq('user_id', userId)
    .eq('is_active', true)
    .order('due_date', { ascending: true });

  if (error) throw error;
  return (data || []) as Bill[];
}

export async function createBill(
  userId: string,
  formData: BillFormData
): Promise<Bill> {
  const { data, error } = await supabase
    .from('bills')
    .insert({
      user_id: userId,
      bill_name: formData.bill_name,
      category: formData.category,
      ownership: formData.ownership,
      company: formData.company || null,
      monthly_amount: formData.monthly_amount,
      due_date: formData.due_date,
      auto_pay: formData.auto_pay,
      bill_status: formData.bill_status,
      notes: formData.notes || null,
    })
    .select()
    .single();

  if (error) throw error;
  return data as Bill;
}

export async function updateBill(
  billId: string,
  updates: Partial<BillFormData>
): Promise<Bill> {
  const { data, error } = await supabase
    .from('bills')
    .update(updates)
    .eq('id', billId)
    .select()
    .single();

  if (error) throw error;
  return data as Bill;
}

export async function deleteBill(billId: string): Promise<void> {
  const { error } = await supabase
    .from('bills')
    .update({ is_active: false })
    .eq('id', billId);

  if (error) throw error;
}

export async function updateBillStatus(
  billId: string,
  status: BillStatus
): Promise<void> {
  const { error } = await supabase
    .from('bills')
    .update({ bill_status: status })
    .eq('id', billId);

  if (error) throw error;
}

// ─── Default Bills Seed ───────────────────────────────────────────────────────

export const DEFAULT_PERSONAL_BILLS: Omit<BillFormData, 'notes'>[] = [
  { bill_name: 'Mortgage Support (Theressa)', category: 'Mortgage', ownership: 'Personal', company: 'Theressa Jefferson', monthly_amount: 0, due_date: 1, auto_pay: false, bill_status: 'Unpaid' },
  { bill_name: 'Cell Phone', category: 'Cell Phone', ownership: 'Personal', company: '', monthly_amount: 0, due_date: 1, auto_pay: false, bill_status: 'Unpaid' },
  { bill_name: 'Auto Insurance', category: 'Insurance', ownership: 'Personal', company: '', monthly_amount: 0, due_date: 1, auto_pay: false, bill_status: 'Unpaid' },
  { bill_name: 'Health Insurance', category: 'Insurance', ownership: 'Personal', company: '', monthly_amount: 0, due_date: 1, auto_pay: false, bill_status: 'Unpaid' },
  { bill_name: 'Credit Cards', category: 'Credit Cards', ownership: 'Personal', company: '', monthly_amount: 0, due_date: 1, auto_pay: false, bill_status: 'Unpaid' },
  { bill_name: 'Fuel', category: 'Fuel', ownership: 'Personal', company: '', monthly_amount: 0, due_date: 1, auto_pay: false, bill_status: 'Unpaid' },
  { bill_name: 'Food Budget', category: 'Other', ownership: 'Personal', company: '', monthly_amount: 0, due_date: 1, auto_pay: false, bill_status: 'Unpaid' },
];

export const DEFAULT_BUSINESS_BILLS: Omit<BillFormData, 'notes'>[] = [
  { bill_name: 'Rocket.new', category: 'Software', ownership: 'Business', company: 'Rocket.new', monthly_amount: 0, due_date: 1, auto_pay: false, bill_status: 'Unpaid' },
  { bill_name: 'OpenAI', category: 'Software', ownership: 'Business', company: 'OpenAI', monthly_amount: 0, due_date: 1, auto_pay: false, bill_status: 'Unpaid' },
  { bill_name: 'Domain Hosting', category: 'Software', ownership: 'Business', company: '', monthly_amount: 0, due_date: 1, auto_pay: false, bill_status: 'Unpaid' },
  { bill_name: 'Park-N-Lot', category: 'Park-N-Lot', ownership: 'Business', company: 'Park-N-Lot', monthly_amount: 0, due_date: 1, auto_pay: false, bill_status: 'Unpaid' },
  { bill_name: 'Cree8tiveminds', category: 'Cree8tiveminds', ownership: 'Business', company: 'Cree8tiveminds LLC', monthly_amount: 0, due_date: 1, auto_pay: false, bill_status: 'Unpaid' },
  { bill_name: 'Cre8Loads', category: 'Cre8Loads', ownership: 'Business', company: 'Cre8Loads LLC', monthly_amount: 0, due_date: 1, auto_pay: false, bill_status: 'Unpaid' },
];

export async function seedDefaultBills(userId: string): Promise<{ seeded: number }> {
  // Check if user already has bills
  const { data: existing } = await supabase
    .from('bills')
    .select('id')
    .eq('user_id', userId)
    .eq('is_active', true)
    .limit(1);

  if (existing && existing.length > 0) {
    return { seeded: 0 };
  }

  const allDefaults = [
    ...DEFAULT_PERSONAL_BILLS.map((b) => ({ ...b, notes: '' })),
    ...DEFAULT_BUSINESS_BILLS.map((b) => ({ ...b, notes: '' })),
  ];

  const rows = allDefaults.map((b) => ({
    user_id: userId,
    bill_name: b.bill_name,
    category: b.category,
    ownership: b.ownership,
    company: b.company || null,
    monthly_amount: b.monthly_amount,
    due_date: b.due_date,
    auto_pay: b.auto_pay,
    bill_status: b.bill_status,
    notes: null,
  }));

  const { error } = await supabase.from('bills').insert(rows);
  if (error) throw error;

  return { seeded: rows.length };
}

// ─── Bill Auto-Detection ──────────────────────────────────────────────────────

export interface DetectedRecurringBill {
  merchant: string;
  estimatedAmount: number;
  occurrences: number;
  suggestedCategory: BillCategory;
  suggestedOwnership: BillOwnership;
  confidence: 'High' | 'Medium' | 'Low';
  alreadyExists: boolean;
}

export function detectRecurringBillsFromTransactions(
  transactions: { merchant: string; amount: number; date: string; transaction_type: string }[],
  existingBills: Bill[]
): DetectedRecurringBill[] {
  // Only look at expense/debit transactions
  const expenses = transactions.filter(
    (t) => t.amount < 0 && (t.transaction_type === 'Expense' || t.transaction_type === 'Transfer')
  );

  // Group by normalized merchant name
  const merchantMap: Record<string, { amounts: number[]; dates: string[] }> = {};
  for (const t of expenses) {
    const key = t.merchant.trim().toLowerCase();
    if (!key || key.length < 2) continue;
    if (!merchantMap[key]) merchantMap[key] = { amounts: [], dates: [] };
    merchantMap[key].amounts.push(Math.abs(t.amount));
    merchantMap[key].dates.push(t.date);
  }

  // Category inference rules
  const categoryRules: { pattern: RegExp; category: BillCategory; ownership: BillOwnership }[] = [
    { pattern: /mortgage|theressa|jefferson/i, category: 'Mortgage', ownership: 'Personal' },
    { pattern: /t-mobile|verizon|at&t|sprint|cell|phone/i, category: 'Cell Phone', ownership: 'Personal' },
    { pattern: /insurance|geico|progressive|allstate|state farm/i, category: 'Insurance', ownership: 'Personal' },
    { pattern: /credit card|chase|capital one|discover|amex|american express/i, category: 'Credit Cards', ownership: 'Personal' },
    { pattern: /fuel|gas station|chandi|love's|pilot|flying j|shell|bp|exxon|chevron/i, category: 'Fuel', ownership: 'Personal' },
    { pattern: /grocery|food|walmart|kroger|aldi|publix|whole foods|trader joe/i, category: 'Other', ownership: 'Personal' },
    { pattern: /rocket\.new|rocketdotnet/i, category: 'Software', ownership: 'Business' },
    { pattern: /openai/i, category: 'Software', ownership: 'Business' },
    { pattern: /domain|hosting|godaddy|namecheap|cloudflare|bluehost/i, category: 'Software', ownership: 'Business' },
    { pattern: /park.n.lot|parknlot/i, category: 'Park-N-Lot', ownership: 'Business' },
    { pattern: /cree8tiveminds/i, category: 'Cree8tiveminds', ownership: 'Business' },
    { pattern: /cre8loads/i, category: 'Cre8Loads', ownership: 'Business' },
    { pattern: /netflix|hulu|disney|spotify|apple|amazon prime|youtube/i, category: 'Subscriptions', ownership: 'Personal' },
    { pattern: /electric|water|gas|utility|utilities|pge|con ed|duke/i, category: 'Utilities', ownership: 'Personal' },
    { pattern: /internet|comcast|xfinity|spectrum|att fiber/i, category: 'Internet', ownership: 'Personal' },
    { pattern: /rent|apartment|lease/i, category: 'Rent', ownership: 'Personal' },
    { pattern: /loan|student loan|car loan|auto loan/i, category: 'Loans', ownership: 'Personal' },
    { pattern: /tax|irs|state tax/i, category: 'Taxes', ownership: 'Business' },
    { pattern: /software|saas|subscription|adobe|microsoft|google workspace/i, category: 'Software', ownership: 'Business' },
  ];

  const existingNames = new Set(existingBills.map((b) => b.bill_name.toLowerCase()));
  const existingCompanies = new Set(existingBills.map((b) => (b.company || '').toLowerCase()));

  const results: DetectedRecurringBill[] = [];

  for (const [key, data] of Object.entries(merchantMap)) {
    if (data.amounts.length < 2) continue; // Need at least 2 occurrences

    const avgAmount = data.amounts.reduce((s, a) => s + a, 0) / data.amounts.length;
    const occurrences = data.amounts.length;

    // Determine confidence
    let confidence: DetectedRecurringBill['confidence'] = 'Low';
    if (occurrences >= 3) confidence = 'High';
    else if (occurrences >= 2) confidence = 'Medium';

    // Infer category
    let suggestedCategory: BillCategory = 'Other';
    let suggestedOwnership: BillOwnership = 'Personal';
    for (const rule of categoryRules) {
      if (rule.pattern.test(key)) {
        suggestedCategory = rule.category;
        suggestedOwnership = rule.ownership;
        break;
      }
    }

    // Check if already exists
    const alreadyExists = existingNames.has(key) || existingCompanies.has(key);

    // Use original casing from first transaction
    const originalMerchant = expenses.find((t) => t.merchant.trim().toLowerCase() === key)?.merchant.trim() || key;

    results.push({
      merchant: originalMerchant,
      estimatedAmount: Math.round(avgAmount * 100) / 100,
      occurrences,
      suggestedCategory,
      suggestedOwnership,
      confidence,
      alreadyExists,
    });
  }

  // Sort: High confidence first, then by occurrences desc
  return results
    .sort((a, b) => {
      const confOrder = { High: 0, Medium: 1, Low: 2 };
      if (confOrder[a.confidence] !== confOrder[b.confidence]) {
        return confOrder[a.confidence] - confOrder[b.confidence];
      }
      return b.occurrences - a.occurrences;
    })
    .slice(0, 30); // Cap at 30 results
}

// ─── Dashboard Stats ──────────────────────────────────────────────────────────

export function computeDashboardStats(bills: Bill[]): BillDashboardStats {
  const today = new Date();
  const currentDay = today.getDate();
  const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();

  let totalMonthly = 0;
  let dueTodayCount = 0;
  let dueThisWeekCount = 0;
  let dueThisMonthCount = 0;
  let overdueCount = 0;
  let overdueTotal = 0;
  let autoPayCount = 0;
  let autoPayTotal = 0;
  let paidCount = 0;
  let unpaidCount = 0;

  for (const bill of bills) {
    totalMonthly += bill.monthly_amount;

    if (bill.bill_status === 'Paid') paidCount++;
    if (bill.bill_status === 'Unpaid' || bill.bill_status === 'Due Soon') unpaidCount++;
    if (bill.bill_status === 'Overdue') {
      overdueCount++;
      overdueTotal += bill.monthly_amount;
    }
    if (bill.auto_pay) {
      autoPayCount++;
      autoPayTotal += bill.monthly_amount;
    }

    const daysUntil = bill.due_date >= currentDay
      ? bill.due_date - currentDay
      : daysInMonth - currentDay + bill.due_date;

    if (daysUntil === 0) dueTodayCount++;
    if (daysUntil <= 7) dueThisWeekCount++;
    if (daysUntil <= 31) dueThisMonthCount++;
  }

  return {
    totalMonthly,
    dueTodayCount,
    dueThisWeekCount,
    dueThisMonthCount,
    overdueCount,
    overdueTotal,
    autoPayCount,
    autoPayTotal,
    paidCount,
    unpaidCount,
  };
}

// ─── Notifications ────────────────────────────────────────────────────────────

export function getNotifications(bills: Bill[]): BillNotification[] {
  const today = new Date();
  const currentDay = today.getDate();
  const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
  const notifications: BillNotification[] = [];

  for (const bill of bills) {
    if (bill.bill_status === 'Paid') continue;

    const daysUntil = bill.due_date >= currentDay
      ? bill.due_date - currentDay
      : daysInMonth - currentDay + bill.due_date;

    if (bill.bill_status === 'Overdue') {
      notifications.push({
        billId: bill.id,
        billName: bill.bill_name,
        amount: bill.monthly_amount,
        dueDate: bill.due_date,
        daysUntilDue: -1,
        type: 'overdue',
      });
    } else if (daysUntil === 0) {
      notifications.push({
        billId: bill.id,
        billName: bill.bill_name,
        amount: bill.monthly_amount,
        dueDate: bill.due_date,
        daysUntilDue: 0,
        type: 'due_today',
      });
    } else if (daysUntil <= 3) {
      notifications.push({
        billId: bill.id,
        billName: bill.bill_name,
        amount: bill.monthly_amount,
        dueDate: bill.due_date,
        daysUntilDue: daysUntil,
        type: 'due_3_days',
      });
    } else if (daysUntil <= 7) {
      notifications.push({
        billId: bill.id,
        billName: bill.bill_name,
        amount: bill.monthly_amount,
        dueDate: bill.due_date,
        daysUntilDue: daysUntil,
        type: 'due_7_days',
      });
    }
  }

  return notifications.sort((a, b) => a.daysUntilDue - b.daysUntilDue);
}

// ─── Reports ──────────────────────────────────────────────────────────────────

export function generateReport(bills: Bill[], type: BillReport['type']): BillReport {
  let filtered = bills;
  let title = '';

  switch (type) {
    case 'monthly':
      title = 'Monthly Bills Report';
      break;
    case 'annual':
      title = 'Annual Bills Report';
      break;
    case 'paid_vs_unpaid':
      title = 'Paid vs Unpaid Report';
      break;
    case 'personal':
      title = 'Personal Bills Report';
      filtered = bills.filter((b) => b.ownership === 'Personal');
      break;
    case 'business':
      title = 'Business Bills Report';
      filtered = bills.filter((b) => b.ownership === 'Business');
      break;
  }

  const totalAmount =
    type === 'annual'
      ? filtered.reduce((s, b) => s + b.monthly_amount * 12, 0)
      : filtered.reduce((s, b) => s + b.monthly_amount, 0);

  let breakdown: { label: string; amount: number; count: number }[] = [];

  if (type === 'paid_vs_unpaid') {
    const paid = filtered.filter((b) => b.bill_status === 'Paid');
    const unpaid = filtered.filter(
      (b) => b.bill_status === 'Unpaid' || b.bill_status === 'Due Soon'
    );
    const overdue = filtered.filter((b) => b.bill_status === 'Overdue');
    breakdown = [
      { label: 'Paid', amount: paid.reduce((s, b) => s + b.monthly_amount, 0), count: paid.length },
      { label: 'Unpaid', amount: unpaid.reduce((s, b) => s + b.monthly_amount, 0), count: unpaid.length },
      { label: 'Overdue', amount: overdue.reduce((s, b) => s + b.monthly_amount, 0), count: overdue.length },
    ];
  } else {
    const byCategory: Record<string, { amount: number; count: number }> = {};
    for (const bill of filtered) {
      if (!byCategory[bill.category]) byCategory[bill.category] = { amount: 0, count: 0 };
      byCategory[bill.category].amount +=
        type === 'annual' ? bill.monthly_amount * 12 : bill.monthly_amount;
      byCategory[bill.category].count++;
    }
    breakdown = Object.entries(byCategory)
      .map(([label, v]) => ({ label, ...v }))
      .sort((a, b) => b.amount - a.amount);
  }

  return { type, title, totalAmount, bills: filtered, breakdown };
}

// ─── AI Features ─────────────────────────────────────────────────────────────

export interface AIBillInsights {
  recurringDetected: { name: string; estimatedAmount: number; frequency: string }[];
  suggestedMissing: string[];
  predictedMonthlyTotal: number;
  cashNeeded: number;
  insights: string[];
}

export async function getAIBillInsights(
  bills: Bill[],
  transactions: { merchant: string; amount: number; date: string; transaction_type: string }[]
): Promise<AIBillInsights> {
  const billSummary = bills.map((b) => ({
    name: b.bill_name,
    category: b.category,
    amount: b.monthly_amount,
    dueDate: b.due_date,
    autoPay: b.auto_pay,
    status: b.bill_status,
  }));

  const recurringTxns = transactions
    .filter((t) => t.transaction_type === 'Expense' && t.amount < 0)
    .slice(0, 100);

  const prompt = `You are a financial AI assistant. Analyze the following bills and transaction history.

CURRENT BILLS:
${JSON.stringify(billSummary, null, 2)}

RECENT TRANSACTIONS (sample):
${JSON.stringify(recurringTxns, null, 2)}

Please provide:
1. Recurring bills detected from transactions that are NOT already in the bills list
2. Suggested missing bills based on common household/business expenses
3. Predicted total monthly expenses for bills
4. Estimated cash needed this month for unpaid/overdue bills
5. 3-5 key insights about the bill portfolio

Respond ONLY with valid JSON in this exact format:
{
  "recurringDetected": [{"name": "string", "estimatedAmount": number, "frequency": "monthly|weekly|annual"}],
  "suggestedMissing": ["string"],
  "predictedMonthlyTotal": number,
  "cashNeeded": number,
  "insights": ["string"]
}`;

  try {
    const response = await getChatCompletion(
      'OPEN_AI',
      'gpt-4.1-mini',
      [{ role: 'user', content: prompt }],
      { max_completion_tokens: 1000 }
    );

    const content = response?.choices?.[0]?.message?.content || '';
    const jsonMatch = content.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      return JSON.parse(jsonMatch[0]) as AIBillInsights;
    }
  } catch {
    // fallback
  }

  // Fallback computed values
  const unpaidBills = bills.filter(
    (b) => b.bill_status === 'Unpaid' || b.bill_status === 'Overdue' || b.bill_status === 'Due Soon'
  );
  return {
    recurringDetected: [],
    suggestedMissing: ['Health Insurance', 'Life Insurance', 'Water Bill', 'Gas Bill'],
    predictedMonthlyTotal: bills.reduce((s, b) => s + b.monthly_amount, 0),
    cashNeeded: unpaidBills.reduce((s, b) => s + b.monthly_amount, 0),
    insights: [
      `You have ${bills.length} active bills totaling $${bills.reduce((s, b) => s + b.monthly_amount, 0).toFixed(2)}/month`,
      `${bills.filter((b) => b.auto_pay).length} bills are on auto-pay`,
      `${bills.filter((b) => b.bill_status === 'Overdue').length} bills are overdue and need immediate attention`,
    ],
  };
}
